import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { authService } from '@services';

const useAuthStore = create(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      permissions: [],
      isAuthenticated: false,
      isLoading: false,

      setUser: (user) => set({ user, isAuthenticated: !!user }),

      setToken: (token) => set({ token }),

      setPermissions: (permissions) => set({ permissions }),

      login: (user, token, permissions = []) =>
        set({
          user,
          token,
          permissions: user?.permissions || permissions,
          isAuthenticated: true,
          isLoading: false,
        }),

      logout: () =>
        set({
          user: null,
          token: null,
          permissions: [],
          isAuthenticated: false,
        }),

      setLoading: (isLoading) => set({ isLoading }),

      // Refresh user data from the server to get updated role and other info
      refreshUser: async () => {
        const { token } = get();
        if (!token) return null;

        try {
          const response = await authService.getProfile();
          if (response.success && response.data) {
            set({
              user: response.data,
              permissions: response.data.permissions || [],
            });
            return response.data;
          }
        } catch (error) {
          console.error('Error refreshing user data:', error);
          // If token is invalid, logout
          if (error.response?.status === 401) {
            get().logout();
          }
        }
        return null;
      },

      // Verificación de roles (legacy)
      hasRole: (role) => {
        const { user } = get();
        return user?.role?.toUpperCase() === role?.toUpperCase();
      },

      hasAnyRole: (roles) => {
        const { user } = get();
        return roles.some((r) => r?.toUpperCase() === user?.role?.toUpperCase());
      },

      // Verificación de permisos
      hasPermission: (permission) => {
        const { permissions } = get();
        if (!permissions || permissions.length === 0) return false;

        // Admin tiene todos los permisos
        if (permissions.some((p) => p.startsWith('admin.'))) return true;

        return permissions.includes(permission);
      },

      hasAnyPermission: (permissionList) => {
        const { hasPermission } = get();
        return permissionList.some((p) => hasPermission(p));
      },

      hasAllPermissions: (permissionList) => {
        const { hasPermission } = get();
        return permissionList.every((p) => hasPermission(p));
      },

      // Verificar acceso a módulo/acción
      canAccess: (module, action) => {
        const { hasPermission } = get();
        return hasPermission(`${module}.${action}`);
      },
    }),
    {
      name: 'agileflow-auth',
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        permissions: state.permissions,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);

export default useAuthStore;
